import React from 'react'
import { APIClient } from '@/services'
import { Module, Strategy } from '@/types'
import { LessonNavigateTo } from '../utils'
import { locationPath, resolveStrategyAction } from '@/utils'
import { ModalLessonDone } from '../components/modals'
import { navigation } from '@/app'
import { Learn } from '@/features'
import { useAuthState } from '@/features/Auth/bridge/useAuthState'
import { missingFrom } from '@/features/Auth/machine/completion'
import { nextCompletionStep } from '@/features/Auth/session/completionRoutes'

const modulesCompleted = new Set<Module['id']>()
export function isModuleCompleted(moduleId: Module['id']): boolean {
  if (modulesCompleted.has(moduleId)) return true

  modulesCompleted.add(moduleId)
  return false
}

export function useLessonLeave(origin: Learn.T.LessonOrigin) {
  const { data: profile } = APIClient.Session.useProfile()
  const { isLoggedIn } = useAuthState()

  const strategies = React.useMemo(() => {
    const nextStep = nextCompletionStep(missingFrom(profile))

    return [
      {
        matches: () => origin === 'onboarding_lesson',
        execute: () => LessonNavigateTo.Feed(),
        name: 'onboarding_lesson'
      },
      {
        matches: ({ chapter, module }) => chapter.is_last_in_module && isLoggedIn && !isModuleCompleted(module.id),
        execute: ({ module }) => ModalLessonDone.open({ origin, type: 'module', name: module.title }),
        name: 'last_in_module'
      },
      {
        matches: () => !!nextStep,
        execute: () => {
          navigation.navigate('Feed.Feed')
          setTimeout(() => {
            LessonNavigateTo.RegisterFlow(nextStep)
          }, 2000)
        },
        name: 'signup_complete'
      },
      {
        matches: () => origin === 'feed',
        execute: () => locationPath.returnToPath(),
        name: 'feed'
      },
      {
        matches: () => true,
        execute: () => LessonNavigateTo.Learn(),
        name: 'default'
      }
    ] satisfies Strategy<Learn.T.LessonStrategyParams>[]
  }, [origin, profile, isLoggedIn])

  const resolve = React.useCallback(
    (params: Learn.T.LessonStrategyParams) => {
      return resolveStrategyAction(params, strategies, `useLessonLeave - ${origin}`)
    },
    [strategies]
  )

  return { resolve }
}
